import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { COMMON_CONTAINER_ID } from '@/config';
import {
  getGoogleAnalyticsClientId,
  getGoogleAnalyticsSessionId,
} from '@/utils';
import { InvoiceRequest } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { paymentSelectors, userSelectors } from '@/redux/selectors';
import { paymentActions } from '@/redux/actions';
import { Button } from '@/components';

import style from './CardCheckoutForm.module.sass';

type CheckoutFormProps = {
  planId: number;
  tariffType?: string;
};

const CardCheckoutForm = ({ planId, tariffType }: CheckoutFormProps) => {
  const [cardComplete, setCardComplete] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { stripeKey, paymentLoading, paymentPlansError } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);

  useEffect(() => {
    if (paymentPlansError) {
      toast.error(paymentPlansError, {
        containerId: COMMON_CONTAINER_ID,
      });
    }

    return () => {
      dispatch(paymentActions.setError(null));
    };
  }, [paymentPlansError]);

  useEffect(() => {
    const createPayment = async () => {
      if (!stripe || !elements) {
        return dispatch(paymentActions.setPaymentLoading(false));
      }

      const result = await stripe.confirmCardPayment(stripeKey as string, {
        payment_method: {
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: userData?.fullName,
            email: userData?.emailAddress,
          },
        },
      });

      if (result.error) {
        toast.error(
          `${result.error.message} ${t('translation.try_again_msg')}`,
          {
            containerId: COMMON_CONTAINER_ID,
          },
        );
      } else {
        toast.success(t('translation.card_deposit_success'), {
          containerId: COMMON_CONTAINER_ID,
        });
      }
      dispatch(paymentActions.setPaymentLoading(false));
    };

    if (!stripeKey) {
      return;
    }

    createPayment();
  }, [stripeKey]);

  const performSubmit = async () => {
    dispatch(paymentActions.setPaymentLoading(true));

    let gClientId;
    let gSessionId;
    try {
      gClientId = await getGoogleAnalyticsClientId();
      gSessionId = await getGoogleAnalyticsSessionId();
    } catch (e) {
      console.log(e);
    }

    const payload: InvoiceRequest = {
      plan: planId,
      asset: 'USD',
      type: 'plan',
      gClientId,
      gSessionId,
    };

    dispatch(paymentActions.createStripeInvoice(payload));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    performSubmit();
  };

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setCardComplete(event.complete);
  };

  return (
    <form className={style.form}>
      <CardElement
        options={{
          disableLink: true,
          hidePostalCode: true,
          disabled: paymentLoading,
        }}
        className={style.card}
        onChange={handleCardChange}
      />
      <Button
        isLoading={paymentLoading}
        className={style.btn}
        disabled={!stripe || !cardComplete}
        onClick={handleSubmit}
      >
        {t('translation.pay_btn')}
      </Button>
    </form>
  );
};

export default CardCheckoutForm;
